import { useState, useEffect, useCallback } from 'react';

const RECAPTCHA_SCRIPT_URL = 'https://www.google.com/recaptcha/api.js';
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

/**
 * Custom hook for Google reCAPTCHA v3
 * Loads the reCAPTCHA script and provides a function to execute reCAPTCHA
 * @returns {Object} { executeRecaptcha, isReady, error }
 */
const useRecaptcha = () => {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if site key is configured
    if (!SITE_KEY) {
      setError('reCAPTCHA site key is not configured');
      return;
    }

    // Check if script is already loaded
    if (window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(() => {
        // Just check if grecaptcha is available, no need to execute
        if (window.grecaptcha.execute) {
          setIsReady(true);
        } else {
          setError('reCAPTCHA is not properly initialized');
        }
      });
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector(`script[src*="${RECAPTCHA_SCRIPT_URL}"]`);
    if (existingScript) {
      // Wait for script to load
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => {
          if (window.grecaptcha.execute) {
            setIsReady(true);
          } else {
            setError('reCAPTCHA is not properly initialized');
          }
        });
      } else {
        existingScript.addEventListener('load', () => {
          if (window.grecaptcha && window.grecaptcha.ready) {
            window.grecaptcha.ready(() => {
              if (window.grecaptcha.execute) {
                setIsReady(true);
              } else {
                setError('reCAPTCHA is not properly initialized');
              }
            });
          }
        });
      }
      return;
    }

    // Create and load script
    const script = document.createElement('script');
    script.src = `${RECAPTCHA_SCRIPT_URL}?render=${SITE_KEY}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => {
          // Check if execute function is available
          if (window.grecaptcha.execute) {
            setIsReady(true);
          } else {
            setError('reCAPTCHA is not properly initialized');
          }
        });
      } else {
        setError('reCAPTCHA script loaded but grecaptcha is not available');
      }
    };

    script.onerror = () => {
      setError('Failed to load reCAPTCHA script');
    };

    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Don't remove the script on unmount as it might be used by other components
      // The script will remain in the DOM for reuse
    };
  }, []);

  /**
   * Execute reCAPTCHA and get a token
   * @param {string} action - Action name for reCAPTCHA (e.g., 'submit_contact', 'register_driver', 'create_request')
   * @returns {Promise<string>} reCAPTCHA token
   */
  const executeRecaptcha = useCallback(
    async (action = 'submit') => {
      if (!SITE_KEY) {
        throw new Error('reCAPTCHA site key is not configured');
      }

      if (!isReady) {
        throw new Error('reCAPTCHA is not ready yet');
      }

      if (!window.grecaptcha) {
        throw new Error('reCAPTCHA script is not loaded');
      }

      try {
        const token = await window.grecaptcha.execute(SITE_KEY, { action });
        return token;
      } catch (err) {
        console.error('reCAPTCHA execution error:', err);
        throw new Error('Failed to execute reCAPTCHA');
      }
    },
    [isReady]
  );

  return { executeRecaptcha, isReady, error };
};

export default useRecaptcha;

